import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { toast } from 'react-toastify';
import md5 from 'md5';
import {
  Form,
  Button,
  Card,
  Table,
  Modal,
  Spinner,
  Alert,
} from 'react-bootstrap';
import styles from '../../../styles.module.css';
import api from '../../../services/api';
import { requestLaudoPresignDownload } from '../../../services/laudoFiles';
import { getPrefix } from '../../../util/utils';
import selectErgoById from '../../../services/ergo';
import FileCarousel from '../../../components/laudos/FileCarousel';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import IcmHeaderImage from '../../../components/IcmHeaderImage';
import generateErgoPDF from './generateErgoPDF';

const ReportHeader = ({ printing }) => (
  <div className={styles.imageReport}>
    <IcmHeaderImage variant="compact" showBlank={!printing} />
  </div>
);

export default function ReportErgo() {
  const { id } = useParams();
  const location = useLocation();
  const {
    printing = false,
    pageBreakA: initialPageBreakA = false,
    pageBreakB: initialPageBreakB = false,
    pageBreakC: initialPageBreakC = false,
  } = location.state || {};
  const { data: ergo, error, isLoading } = useSWR(
    id ? ['ergo', id] : null,
    () => selectErgoById(id)
  );
  const [showModal, setShowModal] = useState(false);
  const [urls, setURLs] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [pageBreakA, setPageBreakA] = useState(initialPageBreakA);
  const [pageBreakB, setPageBreakB] = useState(initialPageBreakB);
  const [pageBreakC, setPageBreakC] = useState(initialPageBreakC);
  const [cpfPaciente, setcpfPaciente] = useState(false);
  const [isResumo, setIsResumo] = useState(false);
  const normalRef = useRef(null);

  const indicacoesResumo = (ergo?.indicacoes || [])
    .map(item => item?.DESCRICAO || item?.FRASE)
    .filter(Boolean);
  const comentariosResumo = (ergo?.comentarios || [])
    .map(item => item?.DESCRICAO || item?.FRASE)
    .filter(Boolean);
  const conclusoesResumo = (ergo?.conclusoes || [])
    .map(item => item?.DESCRICAO || item?.FRASE)
    .filter(Boolean);

  const handlePageBreak = option => {
    if (option === 'A') setPageBreakA(prev => !prev);
    if (option === 'B') setPageBreakB(prev => !prev);
    if (option === 'C') setPageBreakC(prev => !prev);
  };

  const handleCPFPaciente = option => {
    setcpfPaciente(option === 'SIM');
  };

  useEffect(() => {
    async function fetchFiles() {
      try {
        const { data } = await api.get('/files', {
          params: {
            codLaudo: ergo?.CODEXAME,
            procedimento: 'ergo',
          },
        });

        const filteredFiles = data.filter(
          item =>
            item.Key.includes('.webm') ||
            item.Key.includes('.pdf') ||
            item.Key.includes('.mp4')
        );

        setFiles(filteredFiles);
      } catch (error) {
        toast.error(error.response.data.error);
      }
    }

    if (ergo?.CODEXAME) fetchFiles();
  }, [ergo]);

  function removeDiv() {
    window.scrollTo(0, 0);
    window.print();
  }

  async function showFiles() {
    setShowModal(true);
    setLoadingFiles(true);
    try {
      const array = [];

      for (const file of files) {
        const url = await requestLaudoPresignDownload({ key: file.Key });
        const obj = { url, ...file };
        array.push(obj);
      }

      setURLs(array);
    } catch (error) {
      setURLs([]);
      toast.error(error?.response?.data?.error || 'Erro ao carregar imagens!');
    } finally {
      setLoadingFiles(false);
    }
  }

  function increaseFont() {
    const el = normalRef.current;
    if (!el) return;

    const style = window.getComputedStyle(el);
    const currentSize = parseFloat(style.fontSize);

    el.style.fontSize = `${currentSize + 1}px`;
  }

  function decreaseFont() {
    const el = normalRef.current;
    if (!el) return;

    const style = window.getComputedStyle(el);
    const currentSize = parseFloat(style.fontSize);

    el.style.fontSize = `${currentSize - 1}px`;
  }

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo de ergo!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  if (isLoading || !ergo) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  return (
    <>
      <style>
        {`@media print {
          @page {
            margin-top:2cm;
            margin-bottom:2cm;
            margin-left: 2cm;
            margin-right: 2cm;
          }

          #removeMe {
            display: none !important;
          }

          #normal {
            font-size: 1.2em;
          }

          #normal * {
            font-size: inherit;
          }

          .page-break {
            page-break-before: always;
            break-before: always;
          }

          .imageReport {
            display: block;
            text-align: center;
            margin-bottom: 16px;
          }
        }`}
      </style>
      <div className={styles.imageReport}>
        <IcmHeaderImage variant="compact" showBlank={!printing} />
      </div>{' '}
      <div id="removeMe" className="d-flex justify-content-end">
        <Button size="sm" variant="outline-primary" onClick={increaseFont}>
          A+
        </Button>
        <Button size="sm" variant="outline-primary" onClick={decreaseFont}>
          A-
        </Button>
      </div>
      <LaudoCardHeader title="TESTE ERGOMÉTRICO" />
      <Card.Body id="removeMe">
        <div className="mb-0">
          <label className="form-label font-weight-bold">
            Onde deseja a quebra de página?
          </label>
          <div className="d-flex flex-wrap gap-3 justify-content-left align-items-left">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={pageBreakA}
                onChange={() => handlePageBreak('A')}
                id="breakA"
              />
              <label className="form-check-label" htmlFor="breakA">
                Antes da Frequência Cardíaca Prevista
              </label>
            </div>
            <div className="ml-3 form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={pageBreakB}
                onChange={() => handlePageBreak('B')}
                id="breakB"
              />
              <label className="form-check-label" htmlFor="breakB">
                Depois da Frequência Cardíaca Prevista
              </label>
            </div>
            <div className="ml-3 form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={pageBreakC}
                onChange={() => handlePageBreak('C')}
                id="breakC"
              />
              <label className="form-check-label" htmlFor="breakC">
                Antes de Alterações Eletrocardiográficas
              </label>
            </div>
          </div>
        </div>{' '}
        <div className="mb-0">
          <label className="form-label font-weight-bold">
            Imprimir CPF do Paciente?
          </label>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="cpfPaciente"
                value="SIM"
                checked={cpfPaciente === true}
                onChange={() => handleCPFPaciente('SIM')}
              />
              <label className="form-check-label" htmlFor="cpfPacienteSim">
                Sim
              </label>
            </div>

            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="cpfPaciente"
                value="NAO"
                checked={cpfPaciente === false}
                onChange={() => handleCPFPaciente('NAO')}
              />
              <label className="form-check-label" htmlFor="cpfPacienteNao">
                Não
              </label>
            </div>
          </div>
        </div>
        <hr />
      </Card.Body>
      <Card.Body>
        {isResumo ? (
          <div id="normal" ref={normalRef}>
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: '1.8rem' }}>
                ICM - Instituto do Coração de Marília
              </div>
              <div style={{ textDecoration: 'underline', fontSize: '1.8rem' }}>
                Teste Ergométrico em Esteira Rolante
              </div>
              <div style={{ textDecoration: 'underline', fontSize: '1.8rem' }}>
                Monitorização em 13 Canais
              </div>
            </div>

            <hr style={{ borderTop: '1px solid #888' }} />

            <div style={{ fontSize: '1.2rem', lineHeight: 1.35 }}>
              <div>
                <strong>Paciente:</strong> {ergo?.CODPAC} {ergo?.NOMEPAC}
              </div>
              <div>
                <strong>Sexo:</strong> {ergo?.SEXOPAC} &nbsp;&nbsp;
                <strong>Dt Nasc:</strong> {ergo?.dataNasc} &nbsp;&nbsp;
                <strong>Idade:</strong> {ergo?.idadePAC} anos &nbsp;&nbsp;
                <strong>Data do Laudo:</strong> {ergo?.dataErgo}
              </div>
              {/* <div>
                <strong>Médico que Realizou:</strong>{' '}
                {getPrefix(ergo?.CODREALIZOU)} {ergo?.Realizou}
              </div> */}
              <div>
                <strong>Médico que Solicitou:</strong>{' '}
                {getPrefix(ergo?.CODSOLICITANTE)} {ergo?.Solicitou}
              </div>
              <div style={{ textAlign: 'right' }}>
                <strong>Laudo:</strong> {ergo?.CODEXAME}
              </div>
            </div>

            <hr style={{ borderTop: '1px solid #888' }} />

            <div style={{ fontSize: '1.2rem', lineHeight: 1.35 }}>
              <div style={{ textDecoration: 'underline', fontWeight: 700 }}>
                Indicações do Teste:
              </div>
              {indicacoesResumo.length > 0 ? (
                indicacoesResumo.map((texto, idx) => (
                  <div key={`ind-res-${idx}`} style={{ marginLeft: 12 }}>
                    {texto}
                  </div>
                ))
              ) : (
                <div style={{ marginLeft: 12 }}>Sem descrição</div>
              )}

              <div
                style={{
                  textDecoration: 'underline',
                  fontWeight: 700,
                  marginTop: 8,
                }}
              >
                Comentários:
              </div>
              {comentariosResumo.length > 0 ? (
                comentariosResumo.map((texto, idx) => (
                  <div key={`com-res-${idx}`} style={{ marginLeft: 12 }}>
                    {texto}
                  </div>
                ))
              ) : (
                <div style={{ marginLeft: 12 }}>Sem comentários</div>
              )}

              <div
                style={{
                  textDecoration: 'underline',
                  fontWeight: 700,
                  marginTop: 8,
                }}
              >
                Conclusões:
              </div>
              {conclusoesResumo.length > 0 ? (
                conclusoesResumo.map((texto, idx) => (
                  <div key={`con-res-${idx}`} style={{ marginLeft: 12 }}>
                    {texto}
                  </div>
                ))
              ) : (
                <div style={{ marginLeft: 12 }}>Sem conclusões</div>
              )}
            </div>

            <div className={styles.signatureReport} style={{ marginTop: 16 }}>
              <i className="mr-auto">
                Conferido e assinado eletronicamente por:{' '}
              </i>
              <strong className="mr-auto">
                {getPrefix(ergo?.CODREALIZOU)} {ergo?.Realizou}
              </strong>
              <br></br>
              <i className="mr-auto">
                CRM: {ergo?.CRMMED} - RQE: {ergo?.RQE}
              </i>
              <br></br>
              <i className="mr-auto">
                Assinatura Digital: {md5(JSON.stringify(ergo))}
              </i>
            </div>

            <div id="removeMe" className="text-center mt-3">
              <Button onClick={() => removeDiv()}>Imprimir</Button>{' '}
              <Button
                variant="info"
                onClick={() =>
                  generateErgoPDF(
                    ergo,
                    printing ? 'imprimirTimbrado' : 'imprimirNormal',
                    0.9,
                    { pageBreakA, pageBreakB, pageBreakC, cpfPaciente }
                  )
                }
              >
                Imprimir PDF
              </Button>{' '}
              <Button
                variant={isResumo ? 'secondary' : 'outline-secondary'}
                onClick={() => setIsResumo(prev => !prev)}
              >
                {isResumo ? 'Ver Relatório Completo' : 'Ver Relatório Resumido'}
              </Button>{' '}
              <Button variant="success" onClick={showFiles}>
                Ver imagens
              </Button>
            </div>
          </div>
        ) : (
          <div id="normal" ref={normalRef}>
            <Form noValidate className={styles.contentReport}>
              <strong className="mr-auto">Nome do Paciente: </strong>
              {`${ergo?.CODPAC} - ${ergo?.NOMEPAC}`}{' '}
              {cpfPaciente ? `- CPF: ${ergo?.CPFPAC}` : ''}
              <br></br>
              <strong className="mr-auto">Data de Nasc: </strong>
              {`${ergo?.dataNasc}`}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Sexo: </strong>
              {`${ergo?.SEXOPAC}`}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Idade: </strong>
              {`${ergo?.idadePAC} anos`}
              <br></br>
              <strong className="mr-auto">Convênio: </strong>
              {ergo?.SIGLACONV}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Cod Ergo: </strong>
              {ergo?.CODEXAME}
              &nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Data do Exame: </strong>
              {ergo?.dataErgo}
             {/*  <br></br>
              <strong className="mr-auto">
                Médico que Realizou: {getPrefix(ergo?.CODREALIZOU)}{' '}
              </strong>
              {`${ergo?.Realizou}`} */}
              <br></br>
              <strong className="mr-auto">
                Médico que Solicitou: {getPrefix(ergo?.CODSOLICITANTE)}{' '}
              </strong>
              {`${ergo?.Solicitou}`}
              <hr></hr>
              <Form.Group sm controlId="editNOMEMED">
                <strong className="mr-auto">Indicações do Teste</strong>
                {ergo?.indicacoes?.map(indica => (
                  <Form.Text key={indica.DESCRICAO} className="text-muted">
                    {indica.DESCRICAO}
                  </Form.Text>
                ))}
              </Form.Group>
              <Form.Group sm controlId="editNOMEMED">
                <strong className="mr-auto">Queixas</strong>
                {ergo?.queixas?.map(queixa => (
                  <Form.Text key={queixa.FRASE} className="text-muted">
                    {queixa.FRASE}
                  </Form.Text>
                ))}
              </Form.Group>
              <Form.Group sm controlId="editNOMEMED">
                <strong className="mr-auto">Medicamentos</strong>
                {ergo?.medicamentos?.map(medicto => (
                  <Form.Text key={medicto.FRASE} className="text-muted">
                    {medicto.FRASE}
                  </Form.Text>
                ))}
              </Form.Group>
              <Form.Group sm controlId="editNOMEMED">
                <strong className="mr-auto">Fatores de Risco</strong>
                {ergo?.fatoresRisco?.map(fator => (
                  <Form.Text key={fator.DESCRICAO} className="text-muted">
                    {fator.DESCRICAO}
                  </Form.Text>
                ))}
              </Form.Group>
              <Form.Group sm controlId="editNOMEMED">
                <strong className="mr-auto">Atividade Física</strong>
                {ergo?.atividadesFisica?.map(atv => (
                  <Form.Text key={atv.DESCRICAO} className="text-muted">
                    {atv.DESCRICAO}
                  </Form.Text>
                ))}
              </Form.Group>
              <Form.Group sm controlId="editNOMEMED">
                <strong className="mr-auto">Exame Físico</strong>
                <Form.Text className="text-muted">
                  {ergo?.EXAMEFISICO}
                </Form.Text>
              </Form.Group>
              <Form.Group sm controlId="editNOMEMED">
                <strong className="mr-auto">Eletrocardiograma Basal</strong>
                {ergo?.ecgBasal?.map(ecg => (
                  <Form.Text key={ecg.FRASE} className="text-muted">
                    {ecg.FRASE}
                  </Form.Text>
                ))}
              </Form.Group>
              {/* Quebra de página aqui */}
              {pageBreakA && (
                <div className="page-break">
                  <ReportHeader printing={printing} />
                </div>
              )}
              {/* Header na nova página */}
              <strong className="mr-auto">Frequência Cardíaca Prevista</strong>
              <Table
                bordered
                size="sm"
                className="mb-3"
                style={{ width: '700px' }}
              >
                <tbody>
                  <tr>
                    <td>Máxima (FC): {ergo?.FREQMAX} bpm</td>
                    <td>Submáxima (FC): {ergo?.FREQSUB} bpm</td>
                  </tr>
                </tbody>
              </Table>
              <strong className="mr-auto">
                Frequência Cardíaca e Pressão Arterial
              </strong>
              <Table
                bordered
                size="sm"
                className="mb-3"
                style={{ width: '700px' }}
              >
                <tbody>
                  <tr>
                    <td>Deitado (FC): {ergo?.FCDEITADO} bpm</td>
                    <td>PA: {ergo?.PADEITADO} mmHg</td>
                  </tr>
                  <tr>
                    <td>Em Pé (FC): {ergo?.FCPE} bpm</td>
                    <td>PA: {ergo?.PAPE} mmHg</td>
                  </tr>
                </tbody>
              </Table>
              {/* Quebra de página aqui */}
              {pageBreakB && (
                <div className="page-break">
                  <ReportHeader printing={printing} />
                </div>
              )}
              <Form.Group size="sm" controlId="obs">
                <div>
                  <strong className="mr-auto">
                    Protocolo de {ergo?.PROTOCOLO}
                  </strong>
                </div>
                <br />
                <div>
                  <strong className="mr-auto">Estágio</strong>
                </div>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Estágio</th>
                      <th>Tempo(min)</th>
                      <th>{ergo?.PROTOCOLO === 'Rampa' ? 'Vel(Km/h)': 'Vel(mph)'}</th>
                      <th>Inc(%)</th>
                      <th>FC(bpm)</th>
                      <th>PA(mmHg)</th>
                      <th>Sinais/Sintomas</th>
                      <th>Arritmias</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ergo?.estagios?.map(estagio => (
                      <tr key={estagio.CODESTAGIO}>
                        <td>{estagio.CODESTAGIO}</td>
                        <td>{estagio.TEMPO}</td>
                        <td>{estagio.VELOCIDADE}</td>
                        <td>{estagio.INCLINACAO}</td>
                        <td>{estagio.FC}</td>
                        <td>{estagio.PA}</td>
                        <td>{estagio.Sintomas}</td>
                        <td>{estagio.Arritmias}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div>
                  <strong className="mr-auto">Recuperação</strong>
                </div>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Recuperação</th>
                      <th>Tempo(min)</th>
                      <th>FC(bpm)</th>
                      <th>PA(mmHg)</th>
                      <th>Sinais/Sintomas</th>
                      <th>Arritmias</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ergo?.recuperacoes?.map(recup => (
                      <tr key={recup.CODRECUPERACAO}>
                        <td>{recup.CODRECUPERACAO}</td>
                        <td>{recup.TEMPO}</td>
                        <td>{recup.FC}</td>
                        <td>{recup.PA}</td>
                        <td>{recup.Sintomas}</td>
                        <td>{recup.Arritmias}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Form.Group>
              {/* Quebra de página aqui */}
              {pageBreakC && (
                <div className="page-break">
                  <ReportHeader printing={printing} />
                </div>
              )}
              {ergo?.altECG && (
                <Form.Group sm controlId="editNOMEMED">
                  <strong className="mr-auto">
                    Alterações Eletrocardiográficas
                  </strong>
                  {ergo?.altECG?.map(segST => (
                    <Form.Text key={segST.DESCRICAO} className="text-muted">
                      {segST.DESCRICAO}
                    </Form.Text>
                  ))}
                </Form.Group>
              )}
              <Form.Group sm controlId="editNOMEMED">
                <strong className="mr-auto">Arritmias</strong>
                {ergo?.arritmias?.map(arr => (
                  <Form.Text key={arr.DESCRICAO} className="text-muted">
                    {arr.DESCRICAO}
                  </Form.Text>
                ))}
              </Form.Group>
              <Form.Group sm controlId="editNOMEMED">
                <strong className="mr-auto">Comportamento da PA</strong>
                {ergo?.comportamentoPA?.map(compPA => (
                  <Form.Text key={compPA.DESCRICAO} className="text-muted">
                    {compPA.DESCRICAO}
                  </Form.Text>
                ))}
              </Form.Group>
              <Form.Group sm controlId="editNOMEMED">
                <strong className="mr-auto">Comentários</strong>
                {ergo?.comentarios?.map(comentario => (
                  <Form.Text key={comentario.DESCRICAO} className="text-muted">
                    {comentario.DESCRICAO}
                  </Form.Text>
                ))}
              </Form.Group>
              <Form.Group sm controlId="editNOMEMED">
                <strong className="mr-auto">Conclusões</strong>
                {ergo?.conclusoes?.map(conclusao => (
                  <Form.Text key={conclusao.DESCRICAO} className="text-muted">
                    {conclusao.DESCRICAO}
                  </Form.Text>
                ))}
              </Form.Group>
              <Card className="mt-2 text-center">
                <Card.Header>
                  O Resultado do presente laudo não configura necessariamente a
                  presença ou ausência de doença, devendo obrigatoriamente ser
                  correlacionado com demais dados clínicos e exames
                  complementares pertinentes ao caso.
                </Card.Header>
                <Card.Body>
                  {files.length > 0 && (
                    <div className={styles.messageReport}>
                      Imagens disponíveis no site:{' '}
                      <strong>www.icm.com.br (Resultado de Exames)</strong>
                      {/* TEMPORÁRIO */}
                      <br />
                      <span>Código de verificação: {ergo?.CODPAC}</span>
                      <hr></hr>
                    </div>
                  )}
                  <div className={styles.signatureReport}>
                    <i className="mr-auto">
                      Conferido e assinado eletronicamente por:{' '}
                    </i>
                    <strong className="mr-auto">
                      {' '}
                      {getPrefix(ergo?.CODREALIZOU)} {ergo?.Realizou}
                    </strong>
                    <br></br>
                    <i className="mr-auto">
                      CRM: {ergo?.CRMMED} - RQE: {ergo?.RQE}
                    </i>
                    <br></br>
                    <i className="mr-auto">
                      Assinatura Digital: {md5(JSON.stringify(ergo))}
                    </i>
                  </div>
                </Card.Body>

                <Card.Footer id="removeMe">
                  <Button onClick={() => removeDiv()}>Imprimir</Button>{' '}
                  <Button
                    variant="info"
                    onClick={() =>
                      generateErgoPDF(
                        ergo,
                        printing ? 'imprimirTimbrado' : 'imprimirNormal',
                        0.9,
                        { pageBreakA, pageBreakB, pageBreakC, cpfPaciente }
                      )
                    }
                  >
                    Imprimir PDF
                  </Button>{' '}
                  <Button
                    variant={isResumo ? 'secondary' : 'outline-secondary'}
                    onClick={() => setIsResumo(prev => !prev)}
                  >
                    {isResumo
                      ? 'Ver Relatório Completo'
                      : 'Ver Relatório Resumido'}
                  </Button>{' '}
                  <Button variant="success" onClick={showFiles}>
                    Ver imagens
                  </Button>
                </Card.Footer>
              </Card>
            </Form>
          </div>
        )}
      </Card.Body>
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Imagens</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFiles ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}
            >
              <Spinner animation="border" variant="dark" size="lg" />
            </div>
          ) : (
            <FileCarousel urls={urls} setURLs={setURLs} />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
